/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {ToastrService} from 'ngx-toastr';
import * as _ from 'lodash';

import {ApiConfig} from '../../../../components/api/api';
import {AuthService} from '../../../../components/auth/auth.service';
import {AdnLogService} from '../../../../components/util/adn-log.service';
import {AdnApi404NavigationService} from '../../../errors/adn-api-404-navigation.service';
import {BrowserLocationService} from '../../../angular-platform-providers';

@Injectable({
  providedIn: 'root'
})
export class ApiErrorHandlerService {
  private readonly ignoreErrors: any = {400: 400, 401: 401, 404: 404, 422: 422};
  private readonly ignorePaths = ['/version-copy', '/2fa-recovery'];
  private readonly ignore404Urls = [
    '/stats',
    'uiTest=true',
    '/synchronous/visitor',
    'consentstatistic',
    'publicreleasenotes',
    'releasenotes'
  ];
  private counter = 0;
  private noConnectionTriggered = false;

  constructor(
    private readonly adnLog: AdnLogService,
    private readonly toastr: ToastrService,
    private readonly translateService: TranslateService,
    @Inject(BrowserLocationService) private readonly location: any,
    private readonly AuthService: AuthService,
    private readonly adnApi404Navigation: AdnApi404NavigationService
  ) {
  }

  // Arrow properties so $http can extract methods without losing `this`.
  response = (response: any): any => {
    if (_.get(response, ['status']) === 200 && _.get(response, ['config', 'url']).indexOf('/api/') > -1) {
      this.counter = 0;
      if (this.noConnectionTriggered) {
        this.noConnectionTriggered = false;
      }
    }
    return response;
  };

  responseError = (response: any): any => {
    if (_.isUndefined(response.status)) {
      this.adnLog.warn('Seem to have found an unspecified error.', response);
      return Promise.reject(response || {status: -1});
    }
    if (response.status === 500) {
      const method = _.get(response, ['config', 'method'], 'UNKNOWN');
      const url = _.get(response, ['config', 'url'], 'unknown URL');
      const dxId = _.get(response, ['data', 'dxId']);
      const dxIdMessage = dxId ? ' (dxId=' + dxId + ')' : '';
      this.adnLog.rollbar('error', 'API Request' + dxIdMessage + ' failed with status 500: ' + method + ' ' + url, {
        status: response.status,
        dxId: dxId
      });
    }
    if (response.status === 404 && !_.get(response, ['config', 'ignore404']) && this.AuthService.isValidSession() && !this.AuthService.getSession().isExpired()) {
      const hasIgnore404Url = !!_.find(this.ignore404Urls, (igUrl) => {
        return response.config.url.indexOf(igUrl) > 0
          || _.get(response.config, ['params', 'uiTest']) === true
          || _.get(response.config, 'method') === 'HEAD';
      });
      const hasIgnore404LocationUrl = !!_.find(this.ignorePaths, (igUrl) => {
        return (this.location.pathname || '').indexOf(igUrl) > -1;
      });
      if (!hasIgnore404Url && !hasIgnore404LocationUrl) {
        this.adnApi404Navigation.go(response);
      }
    } else if (!this.ignoreErrors[response.status] && !_.get(response.config, 'hasErrorHandler')) {
      if (ApiConfig.obtain().getId() === 'localhost') {
        if (_.get(response.config, 'url', '').indexOf('preview') > -1) {
          // just avoiding the annoying toastr alerts when in dev mode without the ad server up and running.
          return Promise.reject(response);
        }
      }

      const errorCode = _.get(response, ['data', 'parameters', 'errorCode']);
      if (response.status === 0) {
        this.toastr.warning(
          this.translateService.instant('errors.api.noConnection'),
          this.translateService.instant('errors.api.noConnection.title')
        );
        this.noConnectionTriggered = true;
      } else if (response.status === 403) {
        this.toastr.error(
          this.translateService.instant('errors.api.unauthorised')
            + (errorCode ? this.translateService.instant('errors.api.unauthorised.code', {errorCode: errorCode}) : ''),
          this.translateService.instant('errors.api.unauthorised.title')
        );
      } else if (response.status === -1) {
        this.counter++;
        if (this.counter >= 3) {
          this.toastr.warning(
            this.translateService.instant('errors.api.noConnection'),
            this.translateService.instant('errors.api.noConnection.title')
          );
          this.noConnectionTriggered = true;
        }
      } else {
        this.toastr.warning(
          this.translateService.instant('errors.api.generic'),
          this.translateService.instant('errors.api.title')
        );
      }
    }
    return Promise.reject(response);
  };
}
